import "tailwindcss/tailwind.css";
import "../styles/main.scss";
import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import analytics from "../lib/analytics";
import { GrowthBook, GrowthBookProvider } from "@growthbook/growthbook-react";
import { growthbookClient } from "../utils/growthbookClient";

import { store, persistor } from "../store";
import { measurementId } from "../components/TrackingScripts/LogGA";
import { NextIntlClientProvider } from "next-intl";
import Script from "next/script";

const NEXT_PUBLIC_RECAPTCHA_SITE_KEY =
  process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

function MyApp({ Component, pageProps }) {
  const { events, isReady, query } = useRouter();
  const { dir, recaptchaSiteKey = NEXT_PUBLIC_RECAPTCHA_SITE_KEY } = pageProps;
  const [gaClientId, setGaClientId] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    let isMounted = true;
    if (isMounted && isReady && !gaClientId) {
      gtag("get", measurementId, "client_id", (cId) => {
        setGaClientId(cId);
      });
    }
    return () => {
      isMounted = false;
    };
  }, [isReady, gaClientId]);

  useEffect(() => {
    let isMounted = true;
    const { akid } = query;
    if (isMounted && isReady && !userId && akid) {
      const akidSplit = akid.split(".") || [];
      const userId = akidSplit.length === 2 ? akidSplit[1] : null;
      setUserId(userId);
    }
    return () => {
      isMounted = false;
    };
  }, [isReady, userId, query]);

  useEffect(() => {
    // Load features async
    let isMounted = true;
    if (isMounted && isReady && (userId || gaClientId)) {
      growthbookClient.setAttributes({
        id: userId || gaClientId,
        userId,
        clientId: gaClientId,
        url: window.location.href,
      });
    }

    if (isMounted && isReady) {
      growthbookClient.loadFeatures({ autoRefresh: true });
    }

    return () => {
      isMounted = false;
    };
  }, [isReady, userId, gaClientId]);

  useEffect(() => {
    document.body.dir = dir;
  }, [dir]);

  useEffect(() => {
    let isMounted = true;

    if (isMounted && isReady) {
      analytics.page();
    }

    const handleRouteChange = () => {
      analytics.page();
    };

    events.on("routeChangeComplete", handleRouteChange);
    return () => {
      events.off("routeChangeComplete", handleRouteChange);
      isMounted = false;
    };
  }, [events, isReady]);

  return (
    <>
      <Script
        src="https://polyfill.io/v3/polyfill.min.js?version=3.111.0&features=globalThis%2CIntersectionObserverEntry%2CIntersectionObserver%2CMutationObserver%2CResizeObserver%2Cperformance.now%2CArrayBuffer%2CIntl.Locale"
        strategy="beforeInteractive"
      />
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <GrowthBookProvider growthbook={growthbookClient}>
            <GoogleReCaptchaProvider
              reCaptchaKey={recaptchaSiteKey}
              useEnterprise={true}
              scriptProps={{
                async: true,
                defer: false,
                appendTo: "head",
                nonce: undefined,
              }}
            >
              <NextIntlClientProvider
                messages={pageProps?.messages}
                locale={pageProps?.language || "en"}
              >
                <Component {...pageProps} />
              </NextIntlClientProvider>
            </GoogleReCaptchaProvider>
          </GrowthBookProvider>
        </PersistGate>
      </Provider>
    </>
  );
}

export default MyApp;
